import { FluentProvider } from "@fluentui/react-components";
import { createRoot } from "react-dom/client";
import "./globals.css";

import { getCurrentUser, useAuthStore } from "@entities/user-tokens";
import { imLightTheme } from "@features/config";
import { whenOfficeReady } from "@features/office";
import { PaneShell } from "@features/pane-manager";
import { NotifyToaster } from "@features/primitives";

import { AuthGate } from "./AuthGate";
import { wireUidMigration } from "./wireUidMigration";

/* global document globalThis */

/** `sideload:local`（MSW モック）のとき、ブートストラップ前に MSW ブラウザワーカーを起動する。 */
const API_MOCKING = process.env.IM_API_MOCKING === "enabled";

// Trusted Types スパイク (#7): TT 強制時に React / Fluent UI / React Flow の DOM 文字列代入が
// 既定ポリシー経由になるよう、パススルーの default ポリシーを用意する（存在環境のみ）。これにより
// `require-trusted-types-for 'script'` 下でも描画が成立することを検証する。本番では文字列を
// サニタイズする安全なポリシーへ置換する ([[アドインセキュリティ規則]])。
const tt = (
    globalThis as unknown as {
        trustedTypes?: {
            createPolicy?: (name: string, rules: unknown) => unknown;
            defaultPolicy?: unknown;
        };
    }
).trustedTypes;
if (tt?.createPolicy && !tt.defaultPolicy) {
    try {
        tt.createPolicy("default", {
            createHTML: (s: string) => s,
            createScript: (s: string) => s,
            createScriptURL: (s: string) => s,
        });
    } catch {
        // 既に default ポリシーが存在する等。無視する。
    }
}

async function bootstrap(): Promise<void> {
    // モック (sideload:local): 認証/API 呼び出し前に fetch をラップする。Service Worker は Office
    // タスクペインで不安定なため MSW は使わず fetch シムで代替。動的 import で本番バンドルの実行経路に
    // 乗せない ([[MSWモック・API宛先規則]] §4 の趣旨を踏襲)。
    if (API_MOCKING) {
        try {
            const { installFetchMock } = await import("../mocks/installFetchMock");
            installFetchMock();
        } catch (e) {
            // eslint-disable-next-line no-console
            console.error("fetch mock install failed", e);
        }
    }

    await whenOfficeReady();

    // 走査が終わったら設定の鍵を移す。購読はアドインが閉じるまで生きる。
    wireUidMigration();

    const container = document.getElementById("container");
    if (!container) return;
    // HTML に置いた「読み込み中」を落としてから描く（createRoot に任せず明示する）。
    container.replaceChildren();
    const root = createRoot(container);

    // Bootstrap で auth Cookie の有効性を確認し、useAuthStore を seed する。
    // useEffect 経由のデータフェッチではなく一度きりの起動時 fetch なので [[Webコンソールディレクトリー構成]]
    // L36-37 の useEffect 制限に抵触しない。
    try {
        const user = await getCurrentUser();
        if (user) {
            useAuthStore.getState().setAuthenticated(user);
        } else {
            useAuthStore.getState().setUnauthenticated();
        }
    } catch {
        // ネットワーク失敗等。ログインから始めさせる。
        useAuthStore.getState().setUnauthenticated();
    }

    root.render(
        <FluentProvider theme={imLightTheme}>
            <AuthGate content={<PaneShell />} />
            <NotifyToaster />
        </FluentProvider>
    );
}

/**
 * 起動が落ちたことを画面へ出す。
 *
 * **React の前に落ちる場合があるので、素の DOM で書く。** `void bootstrap()` のままだと未処理の
 * 拒否としてコンソールに残るだけで、利用者からは白い画面と区別が付かなかった（記録 37）。
 */
function renderBootFailure(reason: string): void {
    const container = document.getElementById("container");
    if (!container) return;
    container.replaceChildren();
    const wrap = document.createElement("div");
    wrap.style.cssText = "margin:12px;font:13px/1.6 'Segoe UI',system-ui,sans-serif;color:#242424";
    const head = document.createElement("p");
    head.textContent = "アドインを開始できませんでした。";
    const detail = document.createElement("p");
    detail.style.color = "#616161";
    detail.textContent = reason;
    const hint = document.createElement("p");
    hint.style.color = "#616161";
    hint.textContent =
        "ネットワークに接続しているかを確かめて、作業ウィンドウを閉じて開き直してください。";
    wrap.append(head, detail, hint);
    container.append(wrap);
}

void bootstrap().catch((e) => {
    renderBootFailure(e instanceof Error ? e.message : "原因を特定できませんでした");
});
